import {match} from 'ts-pattern'
import {isNullOrEmpty} from '../collection'
import {fmtNum, fmtDate} from '../format'
import {Prettify} from '../type'
import {ForecastQuestionCfg, ForecastQuestionCfgAmount} from './forecast'
import {DateTime} from 'luxon'
import {mapToObj} from 'remeda'

export namespace Fmt {
  export type Size = 'default' | 'short' | 'long'

  export interface OptionSize {
    default: Option
    short: Option
    long: Option
  }

  export type Option = {
    /** format stirng for the number component (excludes units) */
    fmt?: string
    showSign?: boolean
    type?: 'number' | 'date'
    unit?: string
  }

  export interface Options {
    relative: OptionSize
    absolute: OptionSize
  }

  export const fmt = (value?: number, o?: Option) =>
    value == null
      ? ''
      : (o?.showSign ? (value > 0 ? '+' : '') : '') +
        (o?.type == 'date'
          ? fmtDate(DateTime.fromMillis(value)!, {disableRelative: true})
          : fmtNum(value, o?.fmt ?? 'a')) +
        (o?.unit ? `${o.unit}` : '')

  export const makeFormats = ({
    unit,
    ...common
  }: {
    fmt: string
    unit?: {default?: string; long?: string; short?: string}
    showSign?: boolean
    type?: 'number' | 'date'
  }) =>
    mapToObj(['default', 'short', 'long'] as Size[], size => {
      const u = unit?.[size] ?? unit?.default
      return [size, {unit: u, ...common, fmtFull: fmt + (u ? `"${u}"` : '')} as Option & {fmtFull: string}]
    })

  export const getOption = (
    p: {mode: 'relative' | 'absolute'} & (
      | ({forecastType: 'amount'} & Prettify<
          Pick<ForecastQuestionCfgAmount, 'unitLabel' | 'numberFormat' | 'amountType'>
        >)
      | {forecastType: 'likelihood'}
    )
  ) =>
    match(p)
      .with({forecastType: 'likelihood'}, ({mode}) =>
        makeFormats(
          mode == 'relative'
            ? {fmt: 'a', unit: {default: ' pp.', long: ' percentage points', short: ''}, showSign: true}
            : {fmt: 'a', unit: {default: '%', short: '%'}}
        )
      )
      .otherwise(({unitLabel, numberFormat, amountType}) =>
        makeFormats(
          amountType == 'date'
            ? {fmt: 'd LLL yyyy', unit: {default: ''}, type: 'date'}
            : {
                showSign: p.mode == 'relative',
                unit: {default: isNullOrEmpty(unitLabel) ? '' : ` ${unitLabel}`, short: ''},
                fmt: isNullOrEmpty(numberFormat) ? 'a' : numberFormat
              }
        )
      )

  export const getOptions = (
    p:
      | ({forecastType: 'amount'} & Pick<ForecastQuestionCfgAmount, 'unitLabel' | 'numberFormat' | 'amountType'>)
      | {forecastType: 'likelihood'}
  ): Options =>
    match(p)
      .with({forecastType: 'likelihood'}, p => ({
        relative: getOption({mode: 'relative', ...p}),
        absolute: getOption({mode: 'absolute', ...p})
      }))
      .with({forecastType: 'amount'}, p => ({
        relative: getOption({mode: 'relative', ...p}),
        absolute: getOption({mode: 'absolute', ...p})
      }))
      .exhaustive()

  export const optionsLikelihood = getOptions({forecastType: 'likelihood'})
}
